<script>
  import { _ } from "svelte-i18n";
  import { Route, router, meta } from "tinro";
  import { BASE_URL } from "../utilities/config.js";
  import { createEventDispatcher, onMount } from "svelte";
  import { postJsonFile } from "../utilities/Utils.svelte";
  import {
    currentPage,
    skillId,
    cid,
    userId,
    userName,
    assessmentType,
    assessmentId,
    instanceId,
    assessmentState,
    assessmentDefinitionId,
    activationCode,
    invitationNumber,
    invitationDetail,
    initialPhoto,
    readinessCheckCompleted,
  } from "../utilities/DataStore";
  import DeviceChecker from "./components/DeviceChecker.svelte";

  let errorMsg = undefined;
  let loadingState = "NEW";
  let deviceReady = false;

  // The device check is held back until the invitation config has been
  // resolved, so the webcam section is never mounted (and camera permission
  // never requested) for an assessment that turns out to have anti-cheat off.
  let invitationConfigLoaded = false;

  // Only an explicit false disables the webcam check. Null/absent means the
  // invitation inherits the default, and an unreachable or older API leaves it
  // undefined — both keep the check, so anti-cheat is never dropped silently.
  $: webcamEnabled = $invitationDetail?.antiCheatEnabled !== false;

  onMount(async () => {
    if ($assessmentDefinitionId) {
      loadData();
    } else {
      loadingState = "OK";
    }
    checkMicrophonePermission();
    loadInvitationDetail();
  });

  function loadInvitationDetail() {
    if (!$invitationNumber) {
      invitationConfigLoaded = true;
      return;
    }
    if ($invitationDetail?.invitationNumber === $invitationNumber) {
      invitationConfigLoaded = true;
      return;
    }
    fetch(
      BASE_URL + `/public/assessment/invitation/${$invitationNumber}`,
    )
      .then((response) => {
        if (!response.ok) throw new Error("Network response was not ok");
        return response.json();
      })
      .then((data) => {
        $invitationDetail = data;
        console.log("invitationDetail", data);
      })
      .catch((error) => {
        // Keep every check enabled rather than weakening the assessment.
        console.log("error loading invitation detail", error);
      })
      .finally(() => {
        invitationConfigLoaded = true;
      });
  }

  let assessmentDetail;

  let microphonePermissionStatus = "new";

  function loadData() {
    loadingState = "LOADING";
    return fetch(
      BASE_URL + `/public/assessment/definition/${$assessmentDefinitionId}`,
    )
      .then((response) => {
        if (!response.ok) {
          console.log("response error");

          throw new Error("Network response was not ok");
        }
        console.log("response ok", response);
        return response.json();
      })
      .then((data) => {
        assessmentDetail = data;
        console.log("assessmentDetail", assessmentDetail);
        loadingState = "OK";
      })
      .catch((error) => {
        console.log("catch error", error);
        errorMsg = "ERROR_LOADING_ASSESSMENT";
        assessmentDetail = undefined;
        loadingState = "ERROR";
      });
  }

  function confirmStart() {
    $readinessCheckCompleted = true;

    if (!$assessmentDefinitionId) {
      loadingState = "OK";
      $currentPage = "AssessmentSelection";
      return;
    }

    loadingState = "LOADING";
    startAssessmentInstance(
      $userId,
      $userName,
      $assessmentId,
      $assessmentType,
      $skillId,
      $cid,
    )
      .then((data) => {
        $currentPage = "AssessmentOverview";
      })
      .catch((error) => {
        errorMsg = "Error" + error;
      });
  }

  async function startAssessmentInstance(
    clientId,
    clientName,
    assessmentId,
    assessmentDefinitionCode,
    skillId,
    cid,
  ) {
    const assessmentInstance = {
      clientId: clientId,
      clientName: clientName,
      assessmentId: assessmentId,
      status: "NEW",
      assessmentDefinitionCode: assessmentDefinitionCode,
      assessmentDefinitionId: $assessmentDefinitionId,
      skillId: skillId,
      cid: cid,
      invitationNumber: $invitationNumber,
    };

    await postJsonFile(
      BASE_URL + "/public/assessment/instance",
      { "Content-Type": "application/json" },
      {},
      assessmentInstance,
    )
      .then((data) => {
        console.log("send data to sensay", data);
        $assessmentDefinitionId = data.assessmentDefinitionId;
        $assessmentState = data.status;
        $instanceId = data.id;
      })
      .catch((error) => {
        errorMsg = error;
        console.error("error sending data to sensay", error);
        throw new Error(error);
      });
  }

  function checkMicrophonePermission() {
    microphonePermissionStatus = "new";
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      // Request microphone access
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          // The user has granted permission and we have a stream
          console.log("Microphone permission granted.");
          // You can now use the stream for audio processing, recording, etc.
          microphonePermissionStatus = "ok";
        })
        .catch((error) => {
          // Permission was denied, or there was some other error
          console.error(
            "Microphone permission denied or error occurred:",
            error,
          );
          microphonePermissionStatus = "error";
        });
    } else {
      console.error("getUserMedia() not supported in this browser.");
      microphonePermissionStatus = "error";
    }
  }
</script>

<div class="introduction-container">
  <!-- Header with logo -->
  <div class="header-section px-4">
    <div class="logo-container mb-3">
      <span
        ><img
          style="margin-right: 1em;"
          src="assets/img/logo_horizontal_nobg2.png"
          alt="OksenSay"
        /></span
      >
      {#if $activationCode === "NTNU"}
        <span><img src="assets/img/ntnu_emi_logo.png" alt="NTNU" /></span>
      {/if}
    </div>
  </div>

  <!-- Main Content -->
  <div class="content-section px-4 py-5" style="padding-top: 0.85rem !important;">
    <div class="container">
      <!-- Exercise Title -->
      <h1 class="exercise-title mb-3" style="font-size: 1.75rem;">{$_("READY_TO_START")}</h1>

      <!-- Instructions -->
      <div class="mb-4" style="text-align: center; color: #2e4b62; line-height: 1.5; font-size: 1rem;">
        {#if assessmentDetail?.instructions}
          <div class="instructions-text">
            {@html assessmentDetail?.instructions}
          </div>
        {:else}
          <div>{$_("INSTRUCTIONS.PART2")}</div>
        {/if}
      </div>

      {#if invitationConfigLoaded}
        <DeviceChecker {webcamEnabled} on:photoCaptured={(e) => $initialPhoto = e.detail.imageData} on:deviceReady={(e) => deviceReady = e.detail.ready} />
      {/if}

      <!-- The rules stay visible once the device is ready, since the start
           button asks the test-taker to confirm them. -->
      {#if loadingState === "OK" && invitationConfigLoaded}
        <section class="mb-4 readiness-rules" aria-labelledby="readiness-rules-title">
          <h2 id="readiness-rules-title" class="readiness-rules-title">{$_("INTRO_RULES_TITLE")}</h2>
          <ul class="readiness-rules-list">
            <li>
              <span class="readiness-rules-icon" aria-hidden="true">⚠️</span>
              <span>
                <strong>{$_("INTRO_RULE_CONNECTION_TITLE")}</strong>
                {$_("INTRO_RULE_CONNECTION_PREFIX")}
                <a href="mailto:support@oksensay.com">support@oksensay.com</a>
                {$_("INTRO_RULE_CONNECTION_SUFFIX")}
              </span>
            </li>
            <li>
              <span class="readiness-rules-icon" aria-hidden="true">🚫</span>
              <span>
                <strong>{$_("INTRO_RULE_STAY_TITLE")}</strong>
                {$_("INTRO_RULE_STAY_TEXT")}
              </span>
            </li>
          </ul>
        </section>
      {/if}

      <!-- Start Button -->
      <div class="button-section mt-5 text-center">
        {#if loadingState === "OK"}
          <button
            type="button"
            class="start-button"
            on:click={confirmStart}
            disabled={!deviceReady}
          >
            {$_("INTRO_CONFIRM_START")}
          </button>
        {:else}
          <div class="d-flex justify-content-center">
            <div class="spinner-border" role="status">
              <span class="sr-only">{$_("LOADING")}</span>
            </div>
          </div>
        {/if}
      </div>
    </div>
  </div>
</div>

<style>
  :global(body) {
    background-color: #f1f6fb;
  }

  .introduction-container {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background-color: transparent;
  }

  .header-section {
    background: transparent;
    border-bottom: none;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: calc(1rem + 50px) 0 0;
  }

  .logo-container {
    display: flex;
    align-items: center;
    gap: 1em;
    margin-bottom: 0.25rem;
  }

  .logo-container img {
    height: auto;
    max-height: 70px;
  }

  .progress-section {
    background: transparent;
    padding: 0.75rem 2rem 0.5rem;
  }

  .progress-steps {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 auto 0.75rem;
    max-width: 960px;
    width: 100%;
  }

  .progress-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    flex: 1;
    min-width: 0;
  }

  .progress-step:not(:last-child)::after {
    content: "";
    position: absolute;
    top: 23px;
    left: 50%;
    right: -50%;
    height: 3px;
    background-color: #d9e5ec;
    z-index: 1;
  }

  .progress-step.active:not(:last-child)::after {
    background-color: #36b9cc;
  }

  .progress-number {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background-color: #ffffff;
    border: 2px solid #d3dce4;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1.1rem;
    color: #788da1;
    margin-bottom: 0.4rem;
    z-index: 2;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }

  .progress-step.active .progress-number {
    background-color: #00b8d4;
    border-color: #00b8d4;
    color: #fff;
  }

  .progress-label {
    font-size: 0.75rem;
    text-align: center;
    color: #607d8b;
    font-weight: 700;
    text-transform: uppercase;
    line-height: 1.2;
    max-width: 90px;
    white-space: normal;
  }

  .content-section {
    flex-grow: 1;
    padding: 1rem 1rem 3.5rem;
    display: flex;
    justify-content: center;
    align-items: flex-start;
  }

  .content-card {
    width: min(100%, 980px);
    background-color: #ffffff;
    border-radius: 16px;
    box-shadow: 0 10px 28px rgba(35, 71, 110, 0.12);
    padding: 2.25rem 2.25rem 2.5rem;
    border: 1px solid #e3ecf1;
  }

  .exercise-title {
    font-family: "Inter", "Noto Sans", sans-serif;
    font-size: clamp(2rem, 3.4vw, 2.4rem);
    font-weight: 800;
    color: #10243c;
    text-align: center;
    margin-bottom: 1.75rem;
    margin-top: 0;
  }

  .instructions-container {
    background-color: #f7fbff;
    padding: 1.5rem;
    border-radius: 12px;
    border: 1px solid #dceaf3;
    margin-bottom: 1.75rem;
  }

  .instructions-text {
    text-align: left;
    color: #2e4b62;
    line-height: 1.8;
    font-size: 1rem;
  }

  .readiness-rules {
    max-width: 760px;
    margin-left: auto;
    margin-right: auto;
    padding: 1.25rem 1.5rem;
    background-color: #f7fbff;
    border: 1px solid #dceaf3;
    border-radius: 12px;
    color: #2e4b62;
    font-size: 0.95rem;
    line-height: 1.5;
    text-align: left;
  }

  .readiness-rules-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #10243c;
    margin: 0 0 0.75rem;
  }

  .readiness-rules-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .readiness-rules-list li {
    display: flex;
    gap: 0.6rem;
  }

  .readiness-rules-list li + li {
    margin-top: 0.6rem;
  }

  .readiness-rules-icon {
    flex-shrink: 0;
  }

  .readiness-rules a {
    color: #00a896;
    text-decoration: underline;
  }

  .instructions-text :global(ol),
  .instructions-text :global(ul) {
    margin: 0;
    padding-left: 1.4rem;
  }

  .instructions-text :global(li) {
    margin-bottom: 0.65rem;
  }

  .microphone-status {
    text-align: center;
    font-size: 1rem;
    margin-bottom: 0.8rem;
  }

  .status-checking {
    color: #ff9800;
  }

  .status-ok {
    color: #00a35b;
    font-weight: 700;
  }

  .status-error {
    color: #d32f2f;
    font-weight: 700;
  }

  .button-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 1.8rem;
    gap: 0.75rem;
  }

  .start-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: #00a094;
    color: #ffffff;
    border: 0;
    padding: 0.74rem 1.6rem;
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.5;
    border-radius: 8px;
    text-transform: none;
    letter-spacing: normal;
    transition: transform 0.2s ease, filter 0.2s ease;
    box-shadow: 0 5px 12px rgba(0, 152, 135, 0.32);
    width: auto;
    min-width: 0;
    appearance: none;
    -webkit-appearance: none;
  }

  .start-button:hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
  }

  .start-button:disabled {
    background-color: #cbd5e1;
    color: #475569;
    box-shadow: none;
    cursor: not-allowed;
  }

  @media (max-width: 992px) {
    .content-section {
      padding: 1.8rem 0.75rem 2.5rem;
    }

    .content-card {
      padding: 1.75rem 1.25rem;
    }

    .start-button {
      width: auto;
      max-width: none;
    }
  }
</style>
